import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { MESSAGES } from '../../../utils/notify';
import { pick } from '../../../shared/offer-domain/pick';

// REST client for `api/negotiation` (be#451 §E, be#455 §D). Every response is
// the `{ success, message, data, errors }` envelope; callers get `data`.
// `endpoint` is '/negotiation' (Panitia) or '/negotiation/supplier' (Mitra);
// partner/line/cargo actions share one path and the server decides the side.

const request = async (method, path, { body, params } = {}) => {
  const token = getAuthToken();
  if (!token) throw new Error(MESSAGES.authRequired);

  const res = await axios({
    method,
    url: `${api.API_URL}${path}`,
    data: body,
    params,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  // Global axios carries TWO unwrapping response interceptors
  // (helpers/api_helper.js and core/api_helper.js), so the result is the raw
  // response, the envelope, or already `data`, depending on which loaded.
  let payload = res;
  if (payload && payload.headers && 'status' in payload) payload = payload.data;
  if (payload && typeof payload === 'object' && 'success' in payload) {
    return pick(payload, 'data', 'Data');
  }
  return payload ?? null;
};

// ponytail: loads up to 500 packages and pages/filters on the client (stat
// cards need counts per status). Switch to server paging (PageNumber/Search/
// Status are supported) once a tender team has more packages than that.
const LIST_PAGE_SIZE = 500;

const listPackages = async ({ endpoint }) => {
  const data = await request('get', endpoint, {
    params: { PageNumber: 1, PageSize: LIST_PAGE_SIZE },
  });
  return pick(data, 'items', 'Items') || [];
};

const getDetail = (procurementId, { endpoint }) =>
  request('get', `${endpoint}/${procurementId}`);

// body: { date: 'YYYY-MM-DD', time: 'HH:mm', tz: 'Asia/Jakarta' }
const startCargo = (cargoId, body) =>
  request('post', `/negotiation/cargo/${cargoId}/start`, { body });

const extendCargo = (cargoId, body) =>
  request('post', `/negotiation/cargo/${cargoId}/extend`, { body });

// Rounds and accept are per alpha line (be#455 K-6).
// body: { price, operationalTolerance?, operationalToleranceOption?, note? }
const submitRound = (lineId, body) =>
  request('post', `/negotiation/line/${lineId}/round`, { body });

const acceptLine = lineId =>
  request('post', `/negotiation/line/${lineId}/accept`);

// The sender's own last round, within 60 s and before the other side answers.
const withdrawRound = (lineId, roundId) =>
  request('post', `/negotiation/line/${lineId}/round/${roundId}/withdraw`);

// body: { reason? }
const withdrawPartner = (partnerId, body) =>
  request('post', `/negotiation/partner/${partnerId}/withdraw`, { body });

// `after` = createdDate of the newest message already shown (polling).
const getMessages = async (partnerId, { after } = {}) => {
  const data = await request(
    'get',
    `/negotiation/partner/${partnerId}/messages`,
    { params: after ? { after } : undefined }
  );
  return Array.isArray(data) ? data : pick(data, 'items', 'Items') || [];
};

const postMessage = (partnerId, message) =>
  request('post', `/negotiation/partner/${partnerId}/messages`, {
    body: { message },
  });

const negotiationApi = {
  listPackages,
  getDetail,
  startCargo,
  extendCargo,
  submitRound,
  acceptLine,
  withdrawRound,
  withdrawPartner,
  getMessages,
  postMessage,
};

const useNegotiationApi = () => negotiationApi;

export default useNegotiationApi;
